import _ from 'lodash';
import { ACTIVITY_TYPE } from '../../const';

const sectionIdPath =
  'extensions["http://gradiant.org/in2it/schema/xapi/resourceVersion"]["http://gradiant.org/in2it/schema/xapi/sectionId"]';

export class Cmi5 {
  constructor(cmi5, bookId) {
    this.cmi5 = cmi5;
    this._bookId = bookId;
  }

  get title() {
    return this.activity.title;
  }

  get id() {
    return this.cmi5.id;
  }

  get bookId() {
    return this._bookId;
  }

  get format() {
    return this.activity.format;
  }

  /**
   * @returns {boolean}
   */
  get isFromCatalog() {
    return Boolean(this.activity.fromCatalog);
  }

  /**
   * @returns {boolean}
   */
  get isImportedExternalUnit() {
    return Boolean(this.activity.importedExternalUnit);
  }

  /**
   * @returns {boolean}
   */
  get isDisabled() {
    return Boolean(this.activity.disabled);
  }

  /**
   * @returns {string[]}
   */
  get metadata() {
    return _.isArray(this.activity.metadata) ? this.activity.metadata : [];
  }

  /**
   * @returns {string}
   */
  get image() {
    return this.activity.imageUrl;
  }

  /**
   * @returns {string}
   */
  get url() {
    return this.activity.url;
  }

  /**
   * @returns {boolean}
   */
  get hasChildren() {
    return this.getHasChildren();
  }

  /**
   * @returns {string}
   */
  get activityId() {
    return this.activity.id;
  }

  /**
   *
   * @returns {string}
   */
  get sectionId() {
    return this.getSectionId();
  }

  set sectionId(sectionId) {
    _.set(this.activity, sectionIdPath, sectionId);
  }

  /**
   *
   * @returns {string}
   */
  get activityType() {
    return this.getActivityType();
  }

  /**
   *
   * @returns {string}
   */
  get description() {
    return this.activity.description;
  }

  /**
   *
   * @returns {string[]}
   */
  get tags() {
    return this.activity.tags;
  }

  get resources() {
    return this.activity.resources;
  }

  get configuration() {
    return this.activity.configuration || { groups: [], users: [] };
  }

  get adaptation() {
    return this.activity.adaptation || null;
  }

  get originalId() {
    return this.activity.originalId || null;
  }

  /**
   * @returns {Cmi5[]}
   */
  get children() {
    return this.getChildren();
  }

  get deepChildren() {
    return this.getDeepChildren();
  }

  /**
   * @returns {Cmi5[]}
   */
  get activities() {
    return this.getActivitiesWrapper(true);
  }

  /**
   * @returns {string[]}
   */
  get activityIds() {
    return this.activities.map(byActivityId);
  }

  /**
   *
   * @returns {Cmi5[]}
   */
  get lessons() {
    return this.getDeepChildren().filter(isLesson);
  }

  /**
   *
   * @returns {Cmi5[]}
   */
  get units() {
    return this.getDeepChildren().filter(isUnit);
  }

  /**
   * @private
   */
  get activity() {
    if (isCmi5Course(this.cmi5)) {
      return this.cmi5.course;
    }
    return this.cmi5;
  }

  /**
   * @private
   */
  get auOrBlock() {
    return _.isArray(this.cmi5.auOrBlock) ? this.cmi5.auOrBlock : [];
  }

  toJSON() {
    return this.cmi5;
  }

  getCmi5Lesson(unitCmi5Json, activityId) {
    const cmi5Lesson = _.find(unitCmi5Json.auOrBlock, lesson => {
      return lesson.id === activityId;
    });
    return cmi5Lesson;
  }

  getCmi5LauncherLesson(unitCmi5Json, activityId) {
    const cmi5Lesson = this.getCmi5Lesson(unitCmi5Json, activityId);
    return cmi5Lesson.launcher;
  }

  /**
   * @private
   * @returns {Cmi5[]}
   */
  getDeepChildren() {
    return this.getChildren(true);
  }

  /**
   * @private
   * @returns {Cmi5[]}
   */
  getChildren(deep = false) {
    return this.getActivitiesWrapper(deep).slice(1);
  }

  /**
   * @private
   * @returns {Cmi5[]}
   */
  getChildrenByActivityTypes(activityTypes) {
    let children = [];
    this.getChildren().forEach(item => {
      if (activityTypes.indexOf(item.getActivityType()) > -1) {
        children.push(item);
      } else if (item.getHasChildren()) {
        children = _.concat(children, item.getChildrenByActivityTypes(activityTypes));
      }
    });
    return children;
  }

  /**
   * @private
   * @returns {Cmi5[]}
   */
  getHasChildrenByActivityTypes(activityTypes) {
    let hasChildren = false;
    this.getChildren().forEach(item => {
      if (activityTypes.indexOf(item.getActivityType()) > -1) {
        hasChildren = true;
        return;
      } else if (item.getHasChildren()) {
        if (item.getHasChildrenByActivityTypes(activityTypes)) {
          hasChildren = true;
          return;
        }
      }
    });
    return hasChildren;
  }

  /**
   * @private
   * @returns {boolean}
   */
  getHasChildren() {
    return _.isArray(this.cmi5.auOrBlock) && this.cmi5.auOrBlock.length > 0;
  }

  /**
   * @private
   * @returns {string}
   */
  getSectionId() {
    return _.get(this.activity, sectionIdPath);
  }

  /**
   * @private
   * @returns {string}
   */
  getActivityType() {
    return this.activity.activityType;
  }

  /**
   * @private
   * @returns {Cmi5[]}
   */
  getActivitiesWrapper(deep = false) {
    if (!this.hasChildren) {
      return [this];
    }
    const children = _(this.cmi5.auOrBlock)
      .map(block => {
        const cmi5 = new Cmi5(block);
        return deep ? cmi5.getActivitiesWrapper(deep) : cmi5;
      })
      .flatten()
      .value();
    return [this, ...children];
  }

  /**
   * @returns {FormattedBook}
   */
  toBookActivity() {
    return this.toActivity(this);
  }

  /**
   * @param {Cmi5} cmi5
   * @returns {FormattedBook}
   */
  toActivity(cmi5) {
    let content = [];
    if (cmi5.hasChildren) {
      content = _.map(cmi5.children, child => cmi5.toActivity(child));
    }
    const activity = {
      ...Cmi5.toActivityWithoutContent(cmi5),
      content
    };

    if (!_.isEmpty(cmi5.fromCatalog)) activity.fromCatalog = cmi5.fromCatalog;
    if (!_.isEmpty(cmi5.importedExternalUnit)) activity.importedExternalUnit = cmi5.importedExternalUnit;
    if (!_.isEmpty(cmi5.bookId)) activity.id = cmi5.bookId;

    return activity;
  }

  /**
   * @private
   * @param cmi5 {Cmi5}
   */
  static toActivityWithoutContent(cmi5) {
    return {
      name: cmi5.title,
      description: cmi5.description,
      tags: cmi5.tags,
      metadata: cmi5.metadata,
      format: cmi5.format,
      activityId: cmi5.activityId,
      imageUrl: cmi5.image,
      disabled: cmi5.isDisabled,
      url: cmi5.url,
      sectionId: cmi5.sectionId,
      configuration: cmi5.configuration,
      fromCatalog: cmi5.isFromCatalog,
      importedExternalUnit: cmi5.isImportedExternalUnit,
      adaptation: cmi5.adaptation,
      originalId: cmi5.originalId
    };
  }
}

function isCmi5Course(cmi5) {
  return _.has(cmi5, 'course');
}

const isLesson = ({ activityType }) => activityType === ACTIVITY_TYPE.LESSON;
const isUnit = ({ activityType }) => activityType === ACTIVITY_TYPE.UNIT;
const byActivityId = ({ activityId }) => activityId;

/**
 * @typedef {object} FormattedBook
 * @property {string} name
 * @property {string} description
 * @property {Array} tags
 * @property {Array} metadata
 * @property {string} activityId
 * @property {string} imageUrl
 * @property {boolean} disabled
 * @property {string} url
 * @property {string} sectionId
 * @property {{users: string[], groups: string[]}} configuration
 * @property {FormattedBook[]} content
 */
